import { useEffect, useState, useRef } from 'react';

import { AccountCircle, Mic, Send } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { NarrowButton } from '@/components';
import { TabAnimationWrapper } from '@/layouts/TabLayout';
import { canModifyCFS } from '@/services';
import { useUserStore } from '@/store';

import { ICLMessage, IPersonData } from './types';

const personData: IPersonData = {
  name: 'Dominik Strassman',
  gender: 'Male',
  age: '87 years',
  location: 'Room 204A',
};

const initialMsgData: ICLMessage[] = [
  {
    by: 'self',
    content: 'Excepteur sint occaecat',
    timestamp: '11:23 AM',
  },
  {
    by: 'other',
    content: 'Excepteur sint occaecat cupidatat non proident, sunt in culpa...',
    timestamp: '11:23 AM',
  },
  {
    by: 'other',
    content: 'Excepteur sint proident, sunt in culpa',
    timestamp: '11:23 AM',
  },
];

export const CFSCareLinkChatTab = () => {
  const theme = useTheme();
  const { user } = useUserStore();
  const canModify = canModifyCFS(user);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [message, setMessage] = useState<string>('');
  const [msgData, setMsgData] = useState<ICLMessage[]>(initialMsgData);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      setMsgData([
        ...msgData,
        {
          by: 'self',
          content: message,
          timestamp: 'now',
        },
      ]);
    }
    setMessage('');
  };

  useEffect(() => {
    scrollToBottom();
  }, [msgData]);

  return (
    <TabAnimationWrapper>
      <Stack
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          justifyContent="space-between"
          alignItems="center"
          direction="row"
        >
          <Typography
            variant="h2"
            sx={{
              padding: '20px 15px',
              fontWeight: 500,
              fontSize: '1.125rem',
              lineHeight: '20px',
            }}
          >
            Live chat with the nursing center
          </Typography>
        </Stack>
        <Divider />
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              padding: 3,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              flex: 1,
            }}
          >
            <Stack direction="row" width="100%">
              <Stack direction="row" width="100%">
                <AccountCircle color="error" sx={{ paddingRight: '5px' }} />
                <Typography fontSize={15} fontWeight={500}>
                  {personData.name}
                </Typography>
                <Typography
                  color={theme.palette.error.main}
                  sx={{ paddingX: '5px' }}
                >
                  ·
                </Typography>
                <Typography fontSize={15} fontWeight={500}>
                  {personData.gender}
                </Typography>
                <Typography
                  color={theme.palette.error.main}
                  sx={{ paddingX: '5px' }}
                >
                  ·
                </Typography>
                <Typography fontSize={15} fontWeight={500}>
                  {personData.age}
                </Typography>
              </Stack>
              <Box sx={{ flexGrow: 1 }}>
                <Typography fontSize={15} fontWeight={500} noWrap>
                  {personData.location}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack
              spacing={2}
              sx={{
                display: 'flex',
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                position: 'relative',
                height: '100%',
              }}
            >
              {msgData.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent:
                      msg.by === 'self' ? 'flex-end' : 'flex-start',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '80%',
                      backgroundColor:
                        msg.by === 'self' ? '#6E838F' : theme.palette.grey.A100,
                      borderRadius: theme.shape.borderRadius,
                      padding: '10px 15px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color:
                          msg.by === 'self'
                            ? theme.palette.primary.contrastText
                            : theme.palette.secondary.contrastText,
                      }}
                    >
                      {msg.content}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color:
                          msg.by === 'self'
                            ? theme.palette.primary.contrastText
                            : theme.palette.secondary.contrastText,
                        mt: 0.5,
                      }}
                    >
                      {msg.timestamp}
                    </Typography>
                  </Box>
                </Box>
              ))}
              <Box
                ref={messagesEndRef}
                style={{ float: 'left', clear: 'both' }}
              />
            </Stack>
            {canModify && (
              <>
                <Divider />
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-end',
                    flexShrink: 0,
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small">
                            <Mic />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    size="small"
                    multiline
                    maxRows={4}
                  />
                  <NarrowButton
                    onClick={handleSendMessage}
                    sx={{
                      backgroundColor: theme.palette.error.main,
                      color: 'white',
                      '&:hover': { backgroundColor: theme.palette.error.dark },
                    }}
                  >
                    <Send />
                  </NarrowButton>
                </Box>
              </>
            )}
          </Box>
        </Stack>
      </Stack>
    </TabAnimationWrapper>
  );
};
